import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image } from 'react-native';
import { router } from 'expo-router';
import { useFocusEffect } from '@react-navigation/native';
import { Check, CheckCircle2, Camera, Info, ChevronDown, ChevronUp } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, FONT_SIZES, withWeight } from '../../theme';
import { Button, ReadinessMeter } from '../common';
import { useChecklistStore, flushProgressSnapshot } from '../../store/checklistStore';
import { usePostsStore } from '../../store/postsStore';
import { useAuthStore } from '../../store/authStore';
import { ChecklistItem, StructureClaim } from '../../types';
import {
  groupEvidenceForChecklist,
  computeProgressPercent,
  type ItemEvidence,
} from '../../utils/checklistEvidence';
import { ChecklistItemThread } from '../checklist/ChecklistItemThread';
import { ChecklistItemEvidenceList } from '../checklist/ChecklistItemEvidenceList';

interface PropertyChecklistSectionProps {
  structureId: string;
  claim: StructureClaim | null;
  canEdit: boolean;
  /** Pre-expand a single item, e.g. arriving from a post's "View checklist
   *  item" link (`?item=`). */
  initialItemId?: string;
  /** The insurer link's model (`?model=`). Shown on an unclaimed property in
   *  place of the stakeholder default; a claim's own model always wins. */
  linkModelId?: string;
}

/**
 * The property page's featured checklist: readiness meter, model name, and
 * every item with its own expand/collapse, evidence, thread and actions —
 * in place on /structure/[id] rather than behind a drill-in tap. Replaces
 * ChecklistSummary (deleted) and folds in the item detail that used to live
 * on the standalone /structure/checklist/[id] screen (now a redirect).
 */
export function PropertyChecklistSection({
  structureId,
  claim,
  canEdit,
  initialItemId,
  linkModelId,
}: PropertyChecklistSectionProps) {
  const { colors } = useTheme();
  const user = useAuthStore((s) => s.user);
  const {
    selectedChecklist,
    structureProgress,
    fetchChecklistForStructure,
    fetchStructureProgress,
    reconcileProgressSnapshot,
    completeItem,
    uncompleteItem,
    isItemCompleted,
    getCompletedCount,
    getTotalCount,
    isCompleting,
  } = useChecklistStore();
  const { fetchStructureEvidencePosts } = usePostsStore();

  // Narrow deps on purpose: refetch when the resolution INPUTS change, not on
  // every claim object reference change. Both inputs are needed — the default
  // model is stored as an ABSENT insurerChecklistId, so "no claim yet" and
  // "claim on the default model" are indistinguishable by id alone, and a
  // business or municipality claim on its default kept the homeowner
  // checklist resolved during the claimless first render.
  useEffect(() => {
    fetchChecklistForStructure(structureId, claim, linkModelId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    structureId,
    claim?.insurerChecklistId,
    claim?.stakeholderType,
    linkModelId,
    fetchChecklistForStructure,
  ]);

  useEffect(() => {
    if (selectedChecklist) {
      fetchStructureProgress(structureId, selectedChecklist.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structureId, selectedChecklist?.id, fetchStructureProgress]);

  const structureProgressKey = useChecklistStore((s) => s.structureProgressKey);
  const localLoaded =
    selectedChecklist != null && structureProgressKey === `${structureId}_${selectedChecklist.id}`;
  // Until the model and THIS structure's progress are both in, the counts
  // read 0 of 0: the page showed "0%" beside a property with 18 posts for a
  // few seconds on a cold load (Sept 25 2026 walk). A failed fetch shows the
  // meter anyway rather than a placeholder that never resolves.
  const checklistError = useChecklistStore((s) => s.error);
  const checklistReady = localLoaded || checklistError != null;

  // Changing the model leaves the claim's progressSnapshot describing the old
  // one, and that snapshot is what the map coloring and the agent client list
  // read. Correct it here, where the resolved model and its real progress are
  // both in hand. Owner-only (the rules reject anyone else) and the store
  // no-ops unless the numbers actually differ, so this is not a write on
  // every property view.
  useEffect(() => {
    if (!canEdit || !localLoaded) return;
    reconcileProgressSnapshot(structureId, claim?.progressSnapshot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, localLoaded, structureId, structureProgressKey]);

  // Item toggles coalesce their claim progress-snapshot writes behind a
  // debounce; push any pending one when the checklist leaves the screen.
  useEffect(() => () => flushProgressSnapshot(), []);

  const [expandedItem, setExpandedItem] = useState<string | null>(initialItemId ?? null);
  // A link to a different item on an already-mounted property page (e.g.
  // tapping between two "View checklist item" links) should re-expand.
  useEffect(() => {
    if (initialItemId) setExpandedItem(initialItemId);
  }, [initialItemId]);

  const [evidenceByItem, setEvidenceByItem] = useState<Map<string, ItemEvidence>>(new Map());

  // Observation posts tagged with a checklist item, resolved against the
  // model currently on screen. Refetched on focus, not just on mount: adding
  // or editing an observation happens on /post/create, and this screen stays
  // mounted underneath it, so a mount-only effect would leave the new
  // observation missing from the list. fetchStructureEvidencePosts caches
  // for FEED_STALE_MS, so a plain back navigation costs nothing.
  useFocusEffect(
    useCallback(() => {
      if (!structureId || !selectedChecklist) return;
      let stale = false;
      fetchStructureEvidencePosts(structureId, { force: true })
        .then((posts) => {
          if (!stale) setEvidenceByItem(groupEvidenceForChecklist(selectedChecklist, posts));
        })
        .catch(() => {});
      return () => {
        stale = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps -- store action is stable
    }, [structureId, selectedChecklist?.id])
  );

  const handleToggleItem = useCallback((item: ChecklistItem) => {
    setExpandedItem((current) => (current === item.id ? null : item.id));
  }, []);

  const handleUncomplete = useCallback(
    async (item: ChecklistItem) => {
      if (!structureId || !selectedChecklist || !canEdit) return;
      try {
        await uncompleteItem(structureId, selectedChecklist.id, item.id);
      } catch {
        // Best-effort UI; the checkbox reverts on the next store read.
      }
    },
    [structureId, selectedChecklist, uncompleteItem, canEdit]
  );

  const handleAddObservation = useCallback(
    (item: ChecklistItem) => {
      router.push(`/post/create?structureId=${structureId}&checklistItemId=${item.id}`);
    },
    [structureId]
  );

  const handleCompleteWithoutPhoto = useCallback(
    async (item: ChecklistItem) => {
      if (!structureId || !selectedChecklist || !canEdit) return;
      try {
        await completeItem(structureId, selectedChecklist.id, item.id);
      } catch {
        // Best-effort UI; the checkbox reverts on the next store read.
      }
    },
    [structureId, selectedChecklist, completeItem, canEdit]
  );

  const completedCount = getCompletedCount();
  const totalCount = getTotalCount();
  const progressPercent = computeProgressPercent(completedCount, totalCount);
  const modelName = selectedChecklist?.displayName ?? selectedChecklist?.insurerName;
  const items = selectedChecklist?.items ?? [];

  return (
    <View style={styles.wrapper} testID="property-checklist">
      {modelName && (
        <View style={styles.header}>
          <Text style={[styles.overline, { color: colors.textSecondary }]}>Checklist</Text>
          <Text style={[styles.modelName, { color: colors.text }]}>{modelName}</Text>
        </View>
      )}

      {/* At 100% the progress meter gives way to a quiet completion state —
          the readiness loop's payoff deserves a real moment. */}
      {progressPercent === 100 && totalCount > 0 ? (
        <View style={[styles.completePanel, { borderTopColor: colors.border }]}>
          <CheckCircle2 size={32} color={colors.progress} />
          <View style={styles.completeText}>
            <Text style={[styles.completeTitle, { color: colors.progress }]}>
              Checklist complete
            </Text>
            <Text style={[styles.completeSubtitle, { color: colors.textSecondary }]}>
              All {totalCount} items documented
            </Text>
          </View>
        </View>
      ) : (
        <ReadinessMeter
          completedCount={completedCount}
          totalCount={totalCount}
          percentTestID="checklist-progress-percent"
          loading={!checklistReady}
        />
      )}

      {checklistReady &&
        items.map((item) => {
          const completed = isItemCompleted(item.id);
          const isExpanded = expandedItem === item.id;
          const progressItem = structureProgress?.completedItems.find((p) => p.itemId === item.id);
          const evidence = evidenceByItem.get(item.id);
          const evidenceCount = (evidence?.direct.length ?? 0) + (evidence?.related.length ?? 0);
          const Chevron = isExpanded ? ChevronUp : ChevronDown;

          return (
            <View
              key={item.id}
              style={[styles.checklistItem, { borderBottomColor: colors.border }]}>
              <View style={styles.checklistItemHeader}>
                <TouchableOpacity
                  testID={`checklist-check-${item.id}`}
                  onPress={() =>
                    completed ? handleUncomplete(item) : handleCompleteWithoutPhoto(item)
                  }
                  disabled={isCompleting || !canEdit}
                  hitSlop={8}
                  style={[
                    styles.checkbox,
                    { borderColor: completed ? colors.progress : colors.borderStrong },
                    !canEdit && styles.checkboxDisabled,
                  ]}>
                  {completed && <Check size={14} color={colors.progress} />}
                </TouchableOpacity>
                <TouchableOpacity
                  testID={`checklist-expand-${item.id}`}
                  style={styles.checklistItemContent}
                  onPress={() => handleToggleItem(item)}
                  disabled={isCompleting}
                  accessibilityRole="button"
                  accessibilityState={{ expanded: isExpanded }}
                  // RN-web drops accessibilityState, so the audit reads aria-*.
                  aria-expanded={isExpanded}
                  accessibilityLabel={`${item.label}, ${isExpanded ? 'collapse' : 'expand'}`}>
                  <Text
                    style={[
                      styles.checklistItemLabel,
                      { color: colors.text },
                      completed && styles.checklistItemLabelCompleted,
                    ]}>
                    {item.label}
                  </Text>
                  {item.description && (
                    <Text
                      style={[styles.checklistItemDescription, { color: colors.textSecondary }]}>
                      {item.description}
                    </Text>
                  )}
                  {/* Collapsed rows still say what is behind them, so the
                    chevron is worth tapping. */}
                  {!isExpanded && evidenceCount > 0 && (
                    <Text style={[styles.evidenceCount, { color: colors.textTertiary }]}>
                      {evidenceCount === 1 ? '1 observation' : `${evidenceCount} observations`}
                    </Text>
                  )}
                </TouchableOpacity>
                {progressItem?.photoUrl && (
                  <View style={styles.photoIndicator}>
                    <Camera size={14} color={colors.progress} />
                  </View>
                )}
                {/* The chevron sits outside the label's tap target, so it looked
                  like the expand control and did nothing when tapped. Same
                  action, same box, so no layout changes. focusable={false}
                  keeps it out of the tab order and it carries no role or
                  label: the labelled row beside it is the real button and
                  already announces the expanded state, so this is a pointer
                  affordance only. NOT accessible={false} — RN-web silently
                  drops press handling on a Touchable with that prop, which
                  makes the chevron look fixed while staying just as dead. */}
                <TouchableOpacity
                  onPress={() => handleToggleItem(item)}
                  disabled={isCompleting}
                  hitSlop={8}
                  focusable={false}
                  style={styles.chevron}>
                  <Chevron size={16} color={colors.textTertiary} />
                </TouchableOpacity>
              </View>

              {completed && progressItem?.photoUrl && (
                <View style={styles.photoPreview}>
                  <Image
                    source={{ uri: progressItem.photoUrl }}
                    style={styles.photoImage}
                    resizeMode="cover"
                  />
                </View>
              )}

              {/* Observations documenting this step, including any carried over
                from an equivalent item in another model. Collapsed rows show
                the count instead. */}
              {isExpanded && (
                <ChecklistItemEvidenceList evidence={evidence} currentUserId={user?.id} />
              )}

              {/* Anyone signed in — scout, owner, or agent — can attach an
                observation photo against this item without marking it
                complete. The photo becomes a post linked to
                (structureId, itemId); only the claimer can flip the
                checkbox, so their row also gets Mark Done. */}
              {canEdit && isExpanded && !completed && (
                <View style={[styles.expandedActions, { borderTopColor: colors.divider }]}>
                  <Button
                    title="Add Observation"
                    onPress={() => handleAddObservation(item)}
                    variant="primary"
                    size="sm"
                    leftIcon={Camera}
                    style={{ flex: 1, marginRight: SPACING.sm }}
                  />
                  <Button
                    title="Mark Done"
                    onPress={() => handleCompleteWithoutPhoto(item)}
                    variant="outline"
                    size="sm"
                    leftIcon={Check}
                    style={{ flex: 1 }}
                  />
                </View>
              )}

              {!canEdit && isExpanded && (
                <View style={[styles.expandedActions, { borderTopColor: colors.divider }]}>
                  <Button
                    title="Add Observation"
                    onPress={() => handleAddObservation(item)}
                    variant="primary"
                    size="sm"
                    leftIcon={Camera}
                    style={{ flex: 1 }}
                  />
                </View>
              )}

              {canEdit && isExpanded && completed && (
                <View style={[styles.expandedActions, { borderTopColor: colors.divider }]}>
                  <Button
                    title="Mark Incomplete"
                    onPress={() => handleUncomplete(item)}
                    variant="ghost"
                    size="sm"
                    style={{ flex: 1 }}
                  />
                </View>
              )}

              {/* Expansion is the only gate now. Rendering the thread and the
                evidence for every completed item turned a ten-item checklist
                into a page of message boxes, which is the opposite of the
                "make this look easy" brief. */}
              {isExpanded && (
                <ChecklistItemThread
                  structureId={structureId}
                  claimedBy={claim?.claimedBy}
                  itemId={item.id}
                />
              )}
            </View>
          );
        })}

      {/* The photo tip stays put — it's most useful mid-checklist, not only
          at the empty start where it used to vanish after item one. */}
      {checklistReady && items.length > 0 && (
        <View style={[styles.tipsCard, { borderTopColor: colors.border }]}>
          <View style={styles.tipsHeader}>
            <Info size={16} color={colors.textSecondary} />
            <Text style={[styles.tipsTitle, { color: colors.text }]}>Tips</Text>
          </View>
          <Text style={[styles.tipsText, { color: colors.textSecondary }]}>
            {completedCount === 0
              ? 'Adding photos helps document your compliance and provides proof for insurance companies. Photos are securely stored and only visible to you and authorized agents.'
              : 'Photos strengthen your documentation. Add one to any item with the camera button. They are stored securely and visible only to you and authorized agents.'}
          </Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    gap: SPACING.sm,
  },
  header: {
    marginBottom: SPACING.xs,
  },
  overline: {
    ...TYPOGRAPHY.overline,
    letterSpacing: 1.1,
  },
  modelName: {
    ...TYPOGRAPHY.h4,
    marginTop: 2,
  },
  checkboxDisabled: {
    opacity: 0.4,
  },
  // The payoff moment keeps its progress color, in the icon and the title —
  // not as a filled green block.
  completePanel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.md,
    paddingVertical: SPACING.md,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  completeText: {
    flex: 1,
  },
  completeTitle: {
    ...TYPOGRAPHY.h4,
  },
  completeSubtitle: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 1,
  },
  // --- Checklist items: hairline rows, not filled/outlined cards. A green
  // wash on every completed row turned the screen into a block of color and
  // buried the readiness meter that is supposed to lead. ---
  checklistItem: {
    paddingVertical: SPACING.smd,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  checklistItemHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  // Outline, never a filled green square: "done" stays progress-colored
  // information (border + check glyph) instead of a block of fill on every
  // completed row.
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: SPACING.smd,
    marginTop: 2,
  },
  checklistItemContent: {
    flex: 1,
  },
  checklistItemLabel: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
    fontSize: FONT_SIZES.md,
  },
  checklistItemLabelCompleted: {
    textDecorationLine: 'line-through',
    opacity: 0.7,
  },
  checklistItemDescription: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 4,
  },
  photoIndicator: {
    marginLeft: SPACING.sm,
  },
  chevron: {
    marginLeft: SPACING.sm,
    marginTop: 2,
  },
  evidenceCount: {
    ...TYPOGRAPHY.caption,
    marginTop: 4,
  },
  expandedActions: {
    flexDirection: 'row',
    marginTop: SPACING.md,
    paddingTop: SPACING.md,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  photoPreview: {
    marginTop: SPACING.md,
    borderRadius: BORDER_RADIUS.md,
    overflow: 'hidden',
    alignSelf: 'flex-start',
  },
  photoImage: {
    width: 160,
    height: 120,
  },
  // --- Tips ---
  tipsCard: {
    marginTop: SPACING.sm,
    paddingTop: SPACING.md,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  tipsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: SPACING.sm,
  },
  tipsTitle: {
    ...TYPOGRAPHY.button,
    marginLeft: SPACING.sm,
  },
  tipsText: {
    ...TYPOGRAPHY.bodySmall,
    lineHeight: 19,
  },
});
